import axios from 'axios';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getLoginToken, getApiUrl } from '../helpers/Util';
import { createNotification } from '../helpers/Notification';
import AccessDenied from '../common/AccessDenied';
import { rules, Permission } from "../../abac.config";
import ConfirmBox from "../common/ConfirmBox";
import { TanStackTable, TablePagination } from '../common';
import { createTextColumn } from '../common/tableHelpers';
import { AllowedTo } from "react-abac";

const UserList = () => {
  const loginUserData = useSelector(state => state.loginUserData);
  const navigate = useNavigate();

  const [userData, setUserData] = useState([]);
  const [search, setSearch] = useState("");
  const [isShowConfirmBox, setIsShowConfirmBox] = useState(false);
  const [loader, setLoader] = useState(true);
  const [removeId, setRemoveId] = useState("");
  const [pageChange, setPageChange] = useState(1);
  const [skip, setSkip] = useState(0);
  const [limit, setLimit] = useState(10);
  const [count, setCount] = useState(0);
  const [resDataLen, setResDataLen] = useState(0);

  // Table columns
  const columns = useMemo(() => [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      size: 200,
      enableSorting: true,
      cell: ({ row }) => {
        const image = (row.original.profilePic == undefined || row.original.profilePic == "undefined" || row.original.profilePic == null) ? '/assets/images/profile-pic.jpeg' : row.original.profilePic;
        return (
          <div className="userDetialsMin">
            <img src={image} alt="" className="avatar-xs rounded-circle me-2" onError={event => {
              event.target.src = "/assets/images/profile-pic.jpeg"
              event.onerror = null
            }} />
            <a href="#" className="text-body" title={row.original.name}>{row.original.name}</a>
          </div>
        )
      }
    },
    createTextColumn('email', 'Email ID', 200),
    createTextColumn('phone', 'Phone Number', 150),
    createTextColumn('role', 'User Role', 150),
    createTextColumn('status', 'Status', 120),
    {
      id: 'actions',
      header: 'Action',
      size: 120,
      enableSorting: false,
      cell: ({ row }) => {
        const data = {
          key: "single",
          pageChange: pageChange,
          limit: limit,
          skip: skip,
          search: search
        };
        return (
          <div>
            <td>
              <ul className="list-inline mb-0">
                <li className="list-inline-item">
                  <Link to={{
                    pathname: `/update/${row.original.id}`,
                    state: { data: data }
                  }}>
                    <i className="uil uil-pen font-size-18"></i>
                  </Link>
                </li>
                <AllowedTo perform={Permission.USER_DELETE}>
                  <li className="list-inline-item">
                    <button className="btn btn-link text-danger p-0" onClick={() => {
                      setRemoveId(row.original.id);
                      setIsShowConfirmBox(true);
                    }}>
                      <i className="uil uil-trash-alt font-size-18"></i>
                    </button>
                  </li>
                </AllowedTo>
              </ul>
            </td>
          </div>
        )
      }
    }
  ], [pageChange, limit, skip, search]);

  // Load user data on mount and when pagination/search changes
  useEffect(() => {
    handleUserData();
    // eslint-disable-next-line
  }, []);

  const handleUserData = useCallback(() => {
    localStorage.removeItem('shopMaintenancePaginationConstants');
    localStorage.removeItem('fieldMaintenancePaginationConstants');
    let filters;
    const userListPaginationConst = JSON.parse(localStorage.getItem('userListPaginationConstants'));
    if (userListPaginationConst !== null) {
      const { previousLimit, previousPage, previousSkip, previousSearch } = userListPaginationConst;
      setPageChange(previousPage);
      setLimit(previousLimit);
      setSkip(previousSkip);
      setSearch(previousSearch);
      filters = `?filter[skip]=${previousSkip}&filter[limit]=${previousLimit}`;
      getUserList(filters, previousSearch);
      localStorage.removeItem('userListPaginationConstants');
    } else {
      filters = `?filter[skip]=${skip}&filter[limit]=${limit}`;
      getUserList(filters, search);
    }
    // eslint-disable-next-line
  }, []);

  const getUserList = (filters, searchValue) => {
    setLoader(true);
    const token = getLoginToken();
    const headers = {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "Authorization": `Bearer ${token}`
    };
    const URL = getApiUrl();
    const searchParam = searchValue !== undefined ? searchValue : search;
    const url = `${URL}/users${filters}&filter[search]=${searchParam}`;
    axios.get(url, { headers })
      .then((response) => {
        if (response.status) {
          let responseData = response.data.data.data;
          let responseCount = response.data.data.count;
          setCount(responseCount);
          setResDataLen(responseData.length);
          handleUserList(responseData);
        }
      })
      .catch((error) => {
        setLoader(false);
        if (error.response && error.response.status === 401) {
          createNotification("error", "Your login session has been expired!");
          localStorage.clear();
          navigate('/login');
        } else {
          createNotification("error", error.response?.data?.message || "An error occurred");
        }
      });
  };

  const handleUserList = (responseData) => {
    let listArr = [];
    responseData && responseData.map((item) => {
      let itemData = { ...item };
      itemData.role = itemData.role && itemData.role.title;
      itemData.status = itemData.status.charAt(0).toUpperCase() + itemData.status.slice(1);
      itemData.profilePic = itemData.upload_fkey && itemData.upload_fkey.filePath;
      listArr.push(itemData);
      return itemData;
    });
    setUserData(listArr);
    setLoader(false);
  };

  const handleConfirmModal = useCallback((isYes) => {
    if (isYes) {
      const token = getLoginToken();
      const headers = { "Authorization": `Bearer ${token}` };
      let filters;
      let localSkip = 0;
      let localPageChange = 0;
      if (resDataLen === 1 && pageChange > 1) {
        localPageChange = pageChange - 1;
        localSkip = skip - 1;
        setPageChange(localPageChange);
        setSkip(localSkip);
        filters = `?filter[skip]=${localSkip}&filter[limit]=${limit}`;
      } else {
        filters = `?filter[skip]=${skip}&filter[limit]=${limit}`;
      }
      const URL = getApiUrl();
      let url = `${URL}/users/${removeId}`;
      axios.delete(url, { headers })
        .then((response) => {
          if (response.status === 200) {
            createNotification("success", "User delete successfully!");
            getUserList(filters);
            setRemoveId("");
            setIsShowConfirmBox(false);
            setLoader(false);
          }
        })
        .catch((error) => {
          setRemoveId("");
          setIsShowConfirmBox(false);
          setLoader(false);
          if (error.response && error.response.status === 401) {
            createNotification("error", "Your login session has been expired!");
            localStorage.clear();
            navigate('/login');
          } else {
            createNotification("error", error.response?.data?.message || "An error occurred");
          }
        });
    } else {
      setRemoveId("");
      setIsShowConfirmBox(false);
      setLoader(false);
    }
  }, [removeId, resDataLen, pageChange, skip, limit, navigate]);

  const handleSearchUser = useCallback((event) => {
    setSearch(event.target.value);
    setPageChange(1);
    setSkip(0);
    setLimit(10);
    let filters = `?filter[skip]=0&filter[limit]=10`;
    getUserList(filters, event.target.value);
  }, []);

  const handlePageChange = useCallback((newPage) => {
    const skp = (newPage - 1) * limit;
    setPageChange(newPage);
    setSkip(skp);
    setLoader(true);
    let filters = `?filter[skip]=${skp}&filter[limit]=${limit}`;
    getUserList(filters);
  }, [limit]);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageChange(1);
    setLimit(newSize);
    setSkip(0);
    setLoader(true);
    let filters = `?filter[skip]=0&filter[limit]=${newSize}`;
    getUserList(filters);
  }, []);

  const data = useMemo(() => ({
    key: "single",
    pageChange: pageChange,
    limit: limit,
    skip: skip,
    search: search
  }), [pageChange, limit, skip, search]);

  const roleKey = loginUserData.roleKey;
  const isAccess = (roleKey !== "" && rules[roleKey] && rules[roleKey][Permission.USER_WRITE]) ? true : false;

  return (
    <div>
      {!isAccess ? <AccessDenied /> :
        <div className="main-content">
          <ConfirmBox
            openModal={isShowConfirmBox}
            confirmMsg="Are you sure to delete this user?"
            handleConfirmModal={handleConfirmModal}
          />
          <div className="page-content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-flex align-items-center justify-content-between">
                    <ol className="breadcrumb m-0">
                      <Link to={`/#`}><li className="breadcrumb-item">Home</li></Link>
                      &nbsp; / &nbsp;
                      <Link to={`/user`}><li className="breadcrumb-item">User</li></Link>
                    </ol>
                  </div>
                  <h4 className="mb-0">User List</h4>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12">
                  <div className="card card-block">
                    <div className="card-body">
                      <div className="row mb-2">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <Link to={{
                              pathname: "/adduser",
                              state: { data: data }
                            }}>
                              <span className="btn btn-success waves-effect waves-light">
                                <i className="uil uil-plus"></i> Add New
                              </span>
                            </Link>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-inline float-md-end mb-3">
                            <div className="search-box ms-2">
                              <div className="position-relative">
                                <input
                                  type="text"
                                  className="form-control rounded bg-light border-0"
                                  placeholder="Search..."
                                  value={search}
                                  onChange={handleSearchUser}
                                />
                                <i className="uil uil-search search-icon"></i>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <TanStackTable
                        data={userData}
                        columns={columns}
                        loading={loader}
                        enableSorting={true}
                      />
                      <TablePagination
                        currentPage={pageChange}
                        totalPages={Math.ceil(count / limit)}
                        totalCount={count}
                        pageSize={limit}
                        data={userData}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        pageSizeOptions={[10, 25, 30, 50, 100]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  );
};

export default UserList;